import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';
import { SettingsParamType, createSettingsParams } from '@wix/tpa-settings';
import { BenefitBulletType } from '../../types/common';
import { Alignment } from '../../utils/alignment';
import { isBuilderEditor } from '../../utils/host-environment';
import { ISettingsParamsDefaults } from './defaultParams';

export enum ButtonStyle {
  // @TODO Use proper adjectives: hollow, rounded, solid, rectangular.
  EMPTY_ROUNDED = 0,
  EMPTY_CORNERED = 1,
  FULL_ROUNDED = 2,
  FULL_CORNERED = 3,
}

export enum WidgetPreset {
  PLAN_LIST = 0,
  SINGLE_PLAN = 1,
}

export enum MainViewDisplayType {
  CLASSIC = 0,
  TABS = 1,
}

export type ISettingsParams = {
  planTitleHtmlTag: SettingsParamType.String;
  titleHtmlTag: SettingsParamType.String;
  textAlignment: Alignment;
  buttonStyle: ButtonStyle;
  pageHeaderText: SettingsParamType.Text;
  pageSubtitleText: SettingsParamType.Text;
  buttonText: SettingsParamType.Text;
  addToCartButtonText: SettingsParamType.Text;
  freeTrialButtonText: SettingsParamType.Text;
  ribbonText: SettingsParamType.Text;
  disclaimerText: SettingsParamType.Text;
  showPageTitle: SettingsParamType.Boolean;
  showPageSubtitle: SettingsParamType.Boolean;
  showPlanName: SettingsParamType.Boolean;
  showTagline: SettingsParamType.Boolean;
  showBenefits: SettingsParamType.Boolean;
  showBenefitsDividers: SettingsParamType.Boolean;
  showHighlightedPlan: SettingsParamType.Boolean;
  showDisclaimer: SettingsParamType.Boolean;
  showRibbon: SettingsParamType.Boolean;
  showRegularPlanCardShadow: SettingsParamType.Boolean;
  showHighlightedPlanCardShadow: SettingsParamType.Boolean;
  hiddenPlans: SettingsParamType.String;
  visiblePlans: SettingsParamType.String;
  planOrder: SettingsParamType.String;
  highlightedPlan: SettingsParamType.String;
  highlightedVariant: SettingsParamType.String;
  tabSubtitles: SettingsParamType.String;
  wereNewSettingsOpened: SettingsParamType.Boolean;
  benefitsBullet: BenefitBulletType;
  useVisiblePlans: SettingsParamType.Boolean;
  preset: WidgetPreset;
  useMobileSettingsInStudio: SettingsParamType.Boolean;
  showImage: SettingsParamType.Boolean;
  mainViewDisplayType: MainViewDisplayType;
  visibleTabs: SettingsParamType.String;
};

export type PackagePickerSettingsParams = ReturnType<typeof createSettingsParamsWithDefaults>;

export function createSettingsParamsWithDefaults(defaults: ISettingsParamsDefaults = {}) {
  return createSettingsParams<ISettingsParams>({
    planTitleHtmlTag: {
      getDefaultValue: () => 'h2',
    },
    titleHtmlTag: {
      getDefaultValue: () => 'h1',
    },
    textAlignment: {
      key: 'text-alignment',
      getDefaultValue: () => Alignment.CENTER,
    },
    buttonStyle: {
      key: 'button-style',
      getDefaultValue: () => ButtonStyle.FULL_CORNERED,
    },
    pageHeaderText: {
      type: SettingsParamType.Text,
      key: 'page-title-text',
      getDefaultValue: ({ t, getSettingParamValue }) => {
        const textData = getSettingParamValue({
          key: 'pp-text-tab-data',
          inheritFromAppScope: true,
          getDefaultValue: () => undefined as unknown as string,
        });
        return textData?.['page-title-text'] || t('pp.defaults.pageTitle');
      },
      metadata: ({ t }) => {
        return {
          type: DataType.text,
          defaultValue: {
            type: 'static',
            value: t('pp.defaults.pageTitle'),
          },
        };
      },
    },
    pageSubtitleText: {
      type: SettingsParamType.Text,
      key: 'page-subtitle-text',
      getDefaultValue: ({ t }) => t('pp.defaults.pageSubtitle'),
      metadata: ({ t }) => {
        return {
          type: DataType.text,
          defaultValue: {
            type: 'static',
            value: t('pp.defaults.pageSubtitle'),
          },
        };
      },
    },
    buttonText: {
      type: SettingsParamType.Text,
      key: 'button-text',
      getDefaultValue: ({ t, getSettingParamValue }) => {
        const textData = getSettingParamValue({
          key: 'pp-text-tab-data',
          inheritFromAppScope: true,
          getDefaultValue: () => undefined as unknown as string,
        });

        return textData?.['button-text'] || t('pp.defaults.button-buy-now');
      },
      metadata: ({ t }) => {
        return {
          type: DataType.text,
          defaultValue: {
            type: 'static',
            value: t('pp.defaults.button-buy-now'),
          },
        };
      },
    },
    addToCartButtonText: {
      type: SettingsParamType.Text,
      key: 'add-to-cart-button-text',
      getDefaultValue: ({ t, getSettingParamValue }) => {
        const textData = getSettingParamValue({
          key: 'pp-text-tab-data',
          inheritFromAppScope: true,
          getDefaultValue: () => undefined as unknown as string,
        });
        return textData?.['add-to-cart-button-text'] || t('pp.defaults.button-add-to-cart');
      },
      metadata: ({ t }) => {
        return {
          type: DataType.text,
          defaultValue: {
            type: 'static',
            value: t('pp.defaults.button-add-to-cart'),
          },
        };
      },
    },
    freeTrialButtonText: {
      type: SettingsParamType.Text,
      key: 'free-trial-button-text',
      getDefaultValue: ({ t, getSettingParamValue }) => {
        const textData = getSettingParamValue({
          key: 'pp-text-tab-data',
          inheritFromAppScope: true,
          getDefaultValue: () => undefined as unknown as string,
        });
        return textData?.['free-trial-button-text'] || t('pp.defaults.freeTrialButton');
      },
      metadata: ({ t }) => {
        return {
          type: DataType.text,
          defaultValue: {
            type: 'static',
            value: t('pp.defaults.freeTrialButton'),
          },
        };
      },
    },
    ribbonText: {
      type: SettingsParamType.Text,
      key: 'ribbon-text',
      getDefaultValue: ({ t, getSettingParamValue }) => {
        const textData = getSettingParamValue({
          key: 'pp-text-tab-data',
          inheritFromAppScope: true,
          getDefaultValue: () => undefined as unknown as string,
        });
        return textData?.['ribbon-text'] || t('pp.defaults.ribbon');
      },
    },
    disclaimerText: {
      type: SettingsParamType.Text,
      key: 'disclaimer-text',
      getDefaultValue: ({ t, getSettingParamValue }) => {
        const textData = getSettingParamValue({
          key: 'pp-text-tab-data',
          inheritFromAppScope: true,
          getDefaultValue: () => undefined as unknown as string,
        });
        const defaultText = isBuilderEditor() ? t('pp.defaults.disclaimer') : '';
        return textData?.['disclaimer-text'] || defaultText;
      },
      metadata: ({ t }) => {
        return {
          type: DataType.text,
          defaultValue: {
            type: 'static',
            value: t('pp.defaults.disclaimer'),
          },
        };
      },
    },
    showPageTitle: {
      key: 'show-page-title',
      getDefaultValue: () => {
        return defaults.showPageTitle !== undefined ? defaults.showPageTitle : true;
      },
    },
    showPageSubtitle: {
      key: 'show-page-subtitle',
      getDefaultValue: () => false,
    },
    showPlanName: {
      key: 'show-plan-name',
      getDefaultValue: () => true,
    },
    showTagline: {
      key: 'show-plan-tagline',
      getDefaultValue: () => true,
    },
    showBenefits: {
      key: 'show-plan-benefits',
      getDefaultValue: () => true,
    },
    showBenefitsDividers: {
      key: 'show-benefits-dividers',
      getDefaultValue: () => false,
    },
    showHighlightedPlan: {
      key: 'show-highlighted-plan',
      getDefaultValue: () => true,
    },
    showDisclaimer: {
      key: 'show-disclaimer',
      getDefaultValue: () => false,
    },
    showRibbon: {
      key: 'show-ribbon',
      getDefaultValue: () => true,
    },
    showRegularPlanCardShadow: {
      key: 'show-regular-plan-card-shadow',
      getDefaultValue: () => false,
    },
    showHighlightedPlanCardShadow: {
      key: 'show-highlighted-plan-card-shadow',
      getDefaultValue: ({ getSettingParamValue }) =>
        getSettingParamValue({
          type: SettingsParamType.Boolean,
          key: 'show-regular-plan-card-shadow',
          getDefaultValue: () => false,
        }) || false,
    },
    hiddenPlans: {
      key: 'hidden-plans',
      getDefaultValue: () => '',
    },
    visiblePlans: {
      key: 'visible-plans',
      getDefaultValue: () => '',
    },
    planOrder: {
      key: 'plan-order',
      getDefaultValue: () => '',
    },
    highlightedPlan: {
      key: 'highlighted-plan',
      getDefaultValue: () => '',
    },
    highlightedVariant: {
      key: 'highlighted-variant',
      getDefaultValue: () => '',
    },
    tabSubtitles: {
      key: 'tab-subtitles',
      getDefaultValue: () => '',
    },
    wereNewSettingsOpened: {
      key: 'were-new-settings-opened',
      getDefaultValue: () => !!defaults.wereNewSettingsOpened,
    },
    benefitsBullet: {
      key: 'benefits-bullet',
      getDefaultValue: () => BenefitBulletType.NONE,
    },
    useVisiblePlans: {
      key: 'use-visible-plans',
      getDefaultValue: () => false,
    },
    preset: {
      key: 'preset',
      getDefaultValue: () => WidgetPreset.PLAN_LIST,
    },
    useMobileSettingsInStudio: {
      key: 'use-mobile-settings-in-studio',
      getDefaultValue: () => true,
    },
    showImage: {
      key: 'show-image',
      getDefaultValue: () => false,
    },
    mainViewDisplayType: {
      key: 'main-view-display-type',
      getDefaultValue: () => MainViewDisplayType.CLASSIC,
    },
    visibleTabs: {
      key: 'visible-tabs',
      getDefaultValue: () => '',
    },
  });
}

export default createSettingsParamsWithDefaults();
